import { useEffect, useRef } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"

import type { SourceValue } from "@productlane/zero/queries"
import { SOURCE_VALUES } from "@productlane/zero/queries"

import {
	deserializeNegations,
	NEGATION_PARAM,
	serializeNegations,
} from "@/components/filters"
import { useContactsFilterState } from "./contacts-filter-state"

const NULL_TOKEN = "_null"
const TRUE_TOKEN = "true"
const FALSE_TOKEN = "false"

const KEYS = {
	companyIds: "companies",
	subscribedStates: "subscription",
	sources: "source",
} as const

function deserializeSources(param: string): SourceValue[] {
	if (!param) return []
	return param
		.split(",")
		.filter((v): v is SourceValue =>
			(SOURCE_VALUES as readonly string[]).includes(v),
		)
}

const ALL_KEYS = [...Object.values(KEYS), NEGATION_PARAM] as const
const FILTER_KEY_SET = new Set<string>(ALL_KEYS)

function serializeArr(values: readonly (string | null)[]): string {
	return values.map((v) => (v === null ? NULL_TOKEN : v)).join(",")
}
function deserializeArr(param: string): Array<string | null> {
	if (!param) return []
	return param.split(",").map((v) => (v === NULL_TOKEN ? null : v))
}
function serializeBoolArr(values: readonly boolean[]): string {
	return values.map((v) => (v ? TRUE_TOKEN : FALSE_TOKEN)).join(",")
}
function deserializeBoolArr(param: string): boolean[] {
	if (!param) return []
	return param.split(",").reduce<boolean[]>((acc, v) => {
		if (v === TRUE_TOKEN) acc.push(true)
		else if (v === FALSE_TOKEN) acc.push(false)
		return acc
	}, [])
}

function buildParams(): URLSearchParams {
	const s = useContactsFilterState.getState()
	const params = new URLSearchParams()
	if (s.companyIds.length > 0)
		params.set(KEYS.companyIds, serializeArr(s.companyIds))
	if (s.subscribedStates.length > 0)
		params.set(KEYS.subscribedStates, serializeBoolArr(s.subscribedStates))
	if (s.sources.length > 0) params.set(KEYS.sources, s.sources.join(","))
	const negated = serializeNegations(s.negations, KEYS)
	if (negated) params.set(NEGATION_PARAM, negated)
	return params
}

export function useContactsFilterUrlSync() {
	const search = useSearch({ strict: false })
	const navigate = useNavigate()
	const isInitialised = useRef(false)

	useEffect(() => {
		if (isInitialised.current) return
		isInitialised.current = true

		const hasFilterParams = ALL_KEYS.some((k) => search[k] !== undefined)
		if (hasFilterParams) {
			const companyIds = deserializeArr(search[KEYS.companyIds] ?? "")
			const subscribedStates = deserializeBoolArr(
				search[KEYS.subscribedStates] ?? "",
			)
			const sources = deserializeSources(search[KEYS.sources] ?? "")
			useContactsFilterState.setState({
				negations: deserializeNegations(search[NEGATION_PARAM] ?? "", KEYS),
				companyIds,
				subscribedStates,
				sources,
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		const unsub = useContactsFilterState.subscribe(() => {
			if (!isInitialised.current) return
			const filterParams = buildParams()
			void navigate({
				to: ".",
				search: (prev) => ({
					...Object.fromEntries(
						Object.entries(prev).filter(([key]) => !FILTER_KEY_SET.has(key)),
					),
					...Object.fromEntries(filterParams),
				}),
				replace: true,
			})
		})
		return unsub
	}, [navigate])
}
